import { useMemo } from "react";
import { AuthorBlock } from "../AuthorBlock";
import "./AuthorWithImage.scss";
import { fromISODateToDateFormatted } from "@/utils/dates";
import { useCopyToClipboardCurrentPage } from "@/hooks/useCopyToClipboardCurrentPage";
import { getFullNameInitials } from "@/utils/getFullNameInitials";
import { Row } from "../Row";
import { CopyLink } from "@/icons";
import { splitInTwo } from "@/utils/splitInTwo";
import { ActionIconText } from "../ActionIconText";
import { CardCategory, Col, ShareButton } from "@/components";
import { Tags } from "@/sections";
import clsx from "clsx";

// export type VariantType = "service" | "event" | "insight";

type BaseServiceProps = {
  name: string;
  previewImageUrl?: string;
  previewImageTitle?: string;
  updatedBy?: {
    name?: string;
    surname?: string;
  };
  updatedAt?: string;
  readingTime?: number;
};

type Event = BaseServiceProps & {
  eventCardTitle?: string;
  eventCardDescription?: string;
  eventTitle?: string;
  eventDescriptionTitle?: string;
  eventDescription?: string;
  tags?: string[];
};

type Service = BaseServiceProps;
// const AuthenticatedModeEnum = {
//   AUTHENTICATED: "authenticated",
//   PUBLIC: "public",
// } as const;

// type AuthenticatedModes =
//   (typeof AuthenticatedModeEnum)[keyof typeof AuthenticatedModeEnum];
interface AuthorWithImageCommonProps {
  publishedLabel?: string;
  readingTimeLabel?: string;
  readingMinuteLabel?: string;
  tagsLabelTitle?: string;
  shareLabel?: string;
  copyLinkLabel?: string;
  copiedTextLabel?: string;
  ImageComponent?: "img" | React.ComponentType<any>;
  imageProps?: Record<string, any>;
  isPreview?: boolean;
  modeAuthenticated?: boolean;
  eventCardDescriptionTitle?: string;
  tags?: string[];
}

type ServiceProps = {
  data: Service;
  variant: "service";
} & AuthorWithImageCommonProps;

type EventProps = {
  data: Event;
  variant: "event";
} & AuthorWithImageCommonProps;
//
// type InsightProps = {
//   data: null;
//   variant: "insight";
// } & AuthorWithImageCommonProps;

export type AuthorWithImageProps = ServiceProps | EventProps;

export const AuthorWithImage = ({
  data = {} as Service | Event,
  ImageComponent = "img",
  imageProps = {},
  variant = "service",
  publishedLabel = "Pubblicato",
  readingTimeLabel = "Tempo di lettura",
  readingMinuteLabel = "min",
  tagsLabelTitle = "Argomenti trattati",
  shareLabel = "Condividi",
  copyLinkLabel = "Copia link",
  copiedTextLabel = "Link copiato",
  isPreview = false,
  modeAuthenticated = false,
  eventCardDescriptionTitle,
  tags,
}: AuthorWithImageProps) => {
  const { copyToClipboard, isCopied } = useCopyToClipboardCurrentPage();
  const avatarInitials = useMemo(() => {
    if (!data?.updatedBy) return "-";
    return getFullNameInitials(
      data.updatedBy.name ?? "",
      data.updatedBy.surname ?? "",
    );
  }, [data.updatedBy]);

  const [firstTitleLine, secondTitleLine] = useMemo(() => {
    if (!data.previewImageTitle || data.previewImageTitle.trim() === "") {
      return [null, null];
    }

    const words = data.previewImageTitle.trim().split(/\s+/);

    if (words.length >= 4) {
      return splitInTwo(data.previewImageTitle.toUpperCase());
    }

    return [data.previewImageTitle.toUpperCase(), null];
  }, [data.previewImageTitle]);

  const readingTimeInMinutes = useMemo(() => {
    const readingTime = data.readingTime ?? 0;
    return Math.ceil(Number(readingTime) / 60) || 0;
  }, [data]);

  const renderImage = () => {
    if (!data.previewImageUrl) return null;

    if (variant === "event") {
      const isEvent = (obj: Service | Event): obj is Event => {
        return "eventCardTitle" in obj || "eventTitle" in obj;
      };

      return (
        <div
          className={clsx(
            isPreview ? "event-preview" : "event-image-container",
            modeAuthenticated ? "margin-authenticated ratio-authenticated" : "",
          )}
        >
          <img
            className="event-background"
            src={data.previewImageUrl}
            alt={`Evento ${data.name}`}
          />

          <div className="event-content-overlay">
            <div className={"event-content"}>
              <img
                className="event-main-image"
                src={data.previewImageUrl}
                alt={`Evento ${data.name}`}
              />

              <div className="event-card-container">
                <Col gap={"x32"}>
                  {isEvent(data) &&
                    (data.eventCardTitle || data.eventCardDescription) && (
                      <CardCategory
                        title={
                          isEvent(data) ? (data.eventCardTitle ?? "-") : "-"
                        }
                        className={"w-full"}
                        description={
                          isEvent(data)
                            ? (eventCardDescriptionTitle ?? "-")
                            : "-"
                        }
                        description2={
                          isEvent(data)
                            ? (data.eventCardDescription ?? "-")
                            : "-"
                        }
                        classNameTitle={"text-slate-20 fs-5 md-fs-custom-2"}
                        classNameDescriptionTitle={
                          "text-slate-28 fw-bold fs-6 md-fs-custom"
                        }
                      />
                    )}
                  {tags && tags?.length > 0 && <Tags title={tagsLabelTitle} items={tags ?? []} />}
                </Col>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={clsx(
          isPreview ? "service-detail-image" : "fullscreen-image-container",
          modeAuthenticated ? "margin-authenticated" : "",
        )}
      >
        <ImageComponent
          alt={`Service ${data.name} image`}
          src={data.previewImageUrl}
          style={{ aspectRatio: 21 / 9 }}
          {...imageProps}
        />
        {data.previewImageTitle && data.previewImageTitle.trim() !== "" && (
          <p className={"short-title"}>
            <span>{firstTitleLine}</span>
            {secondTitleLine && <span>{secondTitleLine}</span>}
          </p>
        )}
      </div>
    );
  };

  const content = (
    <>
      <div className={"action-author"}>
        <AuthorBlock
          authorName={
            data?.updatedBy?.name
              ? `${data?.updatedBy?.name} ${data?.updatedBy?.surname}`
              : "-"
          }
          avatar={{ initials: avatarInitials }}
          publishedLabel={publishedLabel}
          publishedDate={
            fromISODateToDateFormatted(data.updatedAt ?? "", "it") ?? "-"
          }
          readingTimeLabel={readingTimeLabel}
          readingTimeValue={readingTimeInMinutes}
          readingMinuteLabel={readingMinuteLabel}
        />
        <Row gap={"x8"} className={"action-link"}>
          <ShareButton textLabel={shareLabel} titleShared={data.name} />
          <div className={"share-divider"}>
            <ActionIconText
              textLabel={isCopied ? copiedTextLabel : copyLinkLabel}
              icon={<CopyLink />}
              handleAction={copyToClipboard}
              classNameText={"fw-bold"}
            />
          </div>
        </Row>
      </div>

      {renderImage()}
    </>
  );

  return isPreview ? (
    <div className={"author-with-image-container-preview"}>{content}</div>
  ) : (
    content
  );
};
